import { useEffect, useState } from 'preact/hooks';
import { api, ApiError } from '../../api.js';
import { t } from '../../i18n/index.js';
import { CoinIcon, Coins } from '../../components/CoinIcon.js';
import { refreshCoins } from '../../coins.js';
import { COIN_REASON_MAX, type CoinAccountView, type CoinConfig, type CoinKind, type CoinLedgerView } from '../../../shared/coins.js';
import type { BjConfig } from '../../../shared/games/blackjack/rules.js';

// AreCoin for the administrator (user decisions 2026-10-01): the settings, every registered player's
// balance, adding or taking away (no limit, with a reason) and the ledger.

type Flash = { ok: boolean; text: string } | null;
interface View {
  config: CoinConfig;
  accounts: CoinAccountView[];
}
const KIND: Record<CoinKind, string> = { start: 'aloitus', refill: 'täydennys', admin: 'ylläpito', bet: 'panos', win: 'voitto', refund: 'palautus', reward: 'palkkio' };
const errorText = (err: unknown) => t.errors[err instanceof ApiError ? err.code : 'network'];
const when = (ms: number | null) => (ms === null ? '–' : new Date(ms).toLocaleString('fi-FI', { dateStyle: 'short', timeStyle: 'short' }));

export function CoinsAdmin() {
  const [view, setView] = useState<View | null>(null);
  const [flash, setFlash] = useState<Flash>(null);
  const [filter, setFilter] = useState('');
  const [chosen, setChosen] = useState<CoinAccountView | null>(null);
  const [logFor, setLogFor] = useState<number | null>(null);
  const load = () =>
    api<View>('GET', '/api/admin/coins')
      .then(setView)
      .catch((err) => setFlash({ ok: false, text: errorText(err) }));
  useEffect(() => {
    void load();
  }, []);
  if (!view) return <p class="muted">{t.admin.autoBingo.loading}</p>;
  const shown = view.accounts.filter((a) => `${a.displayName} ${a.username}`.toLowerCase().includes(filter.trim().toLowerCase()));
  const total = view.accounts.reduce((s, a) => s + (a.balance ?? 0), 0);
  return (
    <div class="stack">
      {flash && (
        <p class={flash.ok ? 'success' : 'error'} role="status">
          {flash.text}
        </p>
      )}
      <ConfigCard config={view.config} onView={setView} onFlash={setFlash} />
      <BlackjackSettings />
      <section class="card form">
        <h2>
          <CoinIcon size={22} /> Pelaajien saldot
        </h2>
        <p class="hint">
          Saldon näkee vain pelaaja itse ja ylläpito. Tili avautuu aloitussaldolla, kun pelaaja käyttää AreCoinia ensimmäisen kerran (tai kun ylläpito
          muuttaa saldoa). Yhteensä liikkeellä <b>{total.toLocaleString('fi-FI')}</b> AreCoinia.
        </p>
        <input value={filter} onInput={(e) => setFilter(e.currentTarget.value)} placeholder="Hae pelaajaa…" />
        <div class="sms-table-wrap sms-scroll">
          <table class="sms-table">
            <thead>
              <tr>
                <th>Pelaaja</th>
                <th>Saldo</th>
                <th>Viimeisin muutos</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {shown.map((a) => (
                <tr key={a.userId}>
                  <td>
                    {a.displayName} <span class="muted">({a.username})</span>
                  </td>
                  <td>{a.balance === null ? <span class="muted">ei vielä tiliä</span> : <Coins amount={a.balance} />}</td>
                  <td>{when(a.lastChange)}</td>
                  <td>
                    <button class="btn btn-small" type="button" onClick={() => setChosen(a)}>
                      ± Muuta
                    </button>
                    <button class="btn btn-small" type="button" onClick={() => setLogFor(a.userId)}>
                      Loki
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      {chosen && (
        <AdjustCard
          account={chosen}
          onDone={(v, text) => {
            setView(v);
            setChosen(null);
            setFlash({ ok: true, text });
            void refreshCoins();
          }}
          onCancel={() => setChosen(null)}
          onFlash={setFlash}
        />
      )}
      <LedgerCard userId={logFor} name={view.accounts.find((a) => a.userId === logFor)?.displayName ?? null} onAll={() => setLogFor(null)} key={`${logFor}-${view.accounts.length}`} />
    </div>
  );
}

function ConfigCard(props: { config: CoinConfig; onView: (v: View) => void; onFlash: (f: Flash) => void }) {
  const [start, setStart] = useState(props.config.startBalance);
  const [refill, setRefill] = useState(props.config.refillTo);
  const save = async (e: Event) => {
    e.preventDefault();
    try {
      props.onView(await api<View>('PUT', '/api/admin/coins/config', { startBalance: start, refillTo: refill }));
      props.onFlash({ ok: true, text: 'AreCoin-asetukset tallennettu.' });
    } catch (err) {
      props.onFlash({ ok: false, text: errorText(err) });
    }
  };
  return (
    <form class="card form" onSubmit={save}>
      <h2>
        <CoinIcon size={22} /> AreCoin – asetukset
      </h2>
      <p class="hint">Areenan leikkivaluutta: ei rahallista arvoa, sitä ei voi ostaa, myydä eikä vaihtaa. Vain rekisteröityneillä pelaajilla on saldo.</p>
      <div class="grid-2">
        <label>
          Aloitussaldo
          <input type="number" min={0} max={1000000} value={start} onInput={(e) => setStart(Number(e.currentTarget.value))} />
        </label>
        <label>
          Päivittäinen täydennys (kerran päivässä tähän asti)
          <input type="number" min={0} max={1000000} value={refill} onInput={(e) => setRefill(Number(e.currentTarget.value))} />
        </label>
      </div>
      <button class="btn btn-primary" type="submit">
        Tallenna asetukset
      </button>
    </form>
  );
}

function AdjustCard(props: { account: CoinAccountView; onDone: (v: View, text: string) => void; onCancel: () => void; onFlash: (f: Flash) => void }) {
  const [amount, setAmount] = useState('');
  const [sign, setSign] = useState<1 | -1>(1);
  const [reason, setReason] = useState('');
  const n = Math.floor(Number(amount.replace(/\s/g, '')));
  const go = async (e: Event) => {
    e.preventDefault();
    if (!Number.isFinite(n) || n <= 0) return props.onFlash({ ok: false, text: 'Anna määrä (kokonaisluku).' });
    if (!reason.trim()) return props.onFlash({ ok: false, text: 'Kirjoita syy (näkyy pelaajan tapahtumissa).' });
    try {
      const v = await api<View>('POST', '/api/admin/coins/adjust', { userId: props.account.userId, amount: sign * n, reason: reason.trim() });
      props.onDone(v, `${props.account.displayName}: ${sign > 0 ? '+' : '−'}${n.toLocaleString('fi-FI')} AreCoinia.`);
    } catch (err) {
      props.onFlash({ ok: false, text: errorText(err) });
    }
  };
  return (
    <form class="card form" onSubmit={go}>
      <h2>Muuta saldoa: {props.account.displayName}</h2>
      <p class="hint">Nykyinen saldo: {props.account.balance === null ? 'ei vielä tiliä (avautuu aloitussaldolla)' : props.account.balance.toLocaleString('fi-FI')}</p>
      <div class="sms-row">
        <select value={String(sign)} onChange={(e) => setSign(e.currentTarget.value === '1' ? 1 : -1)} style={{ flex: '0 0 140px' }}>
          <option value="1">＋ Lisää</option>
          <option value="-1">− Vähennä</option>
        </select>
        <input inputMode="numeric" value={amount} onInput={(e) => setAmount(e.currentTarget.value)} placeholder="määrä" style={{ flex: '0 0 160px' }} />
      </div>
      <label>
        Syy (näkyy pelaajalle)
        <input value={reason} maxLength={COIN_REASON_MAX} onInput={(e) => setReason(e.currentTarget.value)} placeholder="esim. Bingoillan palkinto" />
      </label>
      <div class="bingo-start-actions">
        <button class="btn btn-primary" type="submit">
          Tallenna
        </button>
        <button class="btn btn-small" type="button" onClick={props.onCancel}>
          {t.admin.cancel}
        </button>
      </div>
    </form>
  );
}

function LedgerCard(props: { userId: number | null; name: string | null; onAll: () => void }) {
  const [rows, setRows] = useState<CoinLedgerView[] | null>(null);
  useEffect(() => {
    api<{ ledger: CoinLedgerView[] }>('GET', `/api/admin/coins/ledger${props.userId ? `?user=${props.userId}` : ''}`)
      .then((r) => setRows(r.ledger))
      .catch(() => setRows([]));
  }, [props.userId]);
  return (
    <section class="card form">
      <h2>Tapahtumaloki{props.name ? `: ${props.name}` : ' (uusimmat 200)'}</h2>
      {props.userId !== null && (
        <button class="btn btn-small" type="button" onClick={props.onAll} style={{ justifySelf: 'start' }}>
          Näytä kaikkien tapahtumat
        </button>
      )}
      {!rows ? (
        <p class="muted">{t.admin.autoBingo.loading}</p>
      ) : rows.length === 0 ? (
        <p class="muted">Ei tapahtumia.</p>
      ) : (
        <div class="sms-table-wrap sms-scroll">
          <table class="sms-table coins-history">
            <thead>
              <tr>
                <th>Aika</th>
                <th>Pelaaja</th>
                <th>Tapahtuma</th>
                <th>Muutos</th>
                <th>Saldo</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td>{when(r.createdAt)}</td>
                  <td>{r.user}</td>
                  <td>
                    {KIND[r.kind] ?? r.kind}
                    {r.game ? ` · ${r.game}` : ''}
                    {r.reason ? ` – ${r.reason}` : ''}
                    {r.admin ? <span class="muted"> ({r.admin})</span> : ''}
                  </td>
                  <td class={r.amount > 0 ? 'plus' : 'minus'}>{(r.amount > 0 ? '+' : '') + r.amount.toLocaleString('fi-FI')}</td>
                  <td>{r.balanceAfter.toLocaleString('fi-FI')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/** Blackjack's table limits (user decision 2026-10-01: the administrator sets them). */
export function BlackjackSettings() {
  const [cfg, setCfg] = useState<BjConfig | null>(null);
  const [flash, setFlash] = useState<Flash>(null);
  useEffect(() => {
    api<{ config: BjConfig }>('GET', '/api/admin/blackjack')
      .then((r) => setCfg(r.config))
      .catch((err) => setFlash({ ok: false, text: errorText(err) }));
  }, []);
  if (!cfg) return null;
  const save = async (e: Event) => {
    e.preventDefault();
    try {
      setCfg((await api<{ config: BjConfig }>('PUT', '/api/admin/blackjack', cfg)).config);
      setFlash({ ok: true, text: 'Blackjack-asetukset tallennettu (koskevat seuraavia kierroksia).' });
    } catch (err) {
      setFlash({ ok: false, text: errorText(err) });
    }
  };
  return (
    <form class="card form" onSubmit={save}>
      <h2>♠ Blackjack – pöytien asetukset</h2>
      <p class="hint">Panokset AreCoineina, 5:n välein. Kenkä sekoitetaan, kun siitä on jaettu sekoitusrajan verran.</p>
      {flash && <p class={flash.ok ? 'success' : 'error'}>{flash.text}</p>}
      <div class="grid-2">
        <label>
          Pienin panos
          <input type="number" min={5} step={5} value={cfg.minBet} onInput={(e) => setCfg({ ...cfg, minBet: Number(e.currentTarget.value) })} />
        </label>
        <label>
          Suurin panos
          <input type="number" min={5} step={5} value={cfg.maxBet} onInput={(e) => setCfg({ ...cfg, maxBet: Number(e.currentTarget.value) })} />
        </label>
        <label>
          Pakkoja kengässä
          <input type="number" min={1} max={8} value={cfg.decks} onInput={(e) => setCfg({ ...cfg, decks: Number(e.currentTarget.value) })} />
        </label>
        <label>
          Sekoitusraja (% jaettu)
          <input type="number" min={50} max={90} value={Math.round(cfg.penetration * 100)} onInput={(e) => setCfg({ ...cfg, penetration: Number(e.currentTarget.value) / 100 })} />
        </label>
      </div>
      <button class="btn btn-primary" type="submit">
        Tallenna
      </button>
    </form>
  );
}
